import React, { useEffect, useMemo, useState } from "react";

import { Grid, IconButton, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiOutlineLockClosed, HiOutlineLockOpen } from "react-icons/hi";

import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../components/Form/Input";
import Select from "../../../../../../../components/Form/Select";
import { ExternalList } from "../../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Branch } from "../../../../../../../models/branch";
import { JobMode } from "../../../../../../../models/job.mode";
import { JobType } from "../../../../../../../models/jobType";
import { LeadSource } from "../../../../../../../models/leadsource";
import { Sales as SalesQuote } from "../../../../../../../models/sales";
import { Statuses } from "../../../../../../../models/statuses";
import { User } from "../../../../../../../models/user";
import BranchService from "../../../../../../../services/branch.service";
import JobTypeService from "../../../../../../../services/job.type.service";
import LeadSearchFromService from "../../../../../../../services/lead.source.from.service";
import ModeJobService from "../../../../../../../services/mode.job.service";
import StatusesService from "../../../../../../../services/statuses.service";
import UserService from "../../../../../../../services/user.service";
import { handleGetConvertValue, handleSaveConvertValue } from "../../../../../../../util/converts";
import { decimalMask, limitDecimalPlaces } from "../../../../../../../util/decimalFunctions";
import {
  DESTINATION,
  InventoryLink,
  JobSubStatus,
  Status,
  UsedByStatuses,
  UserTypes,
} from "../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import RelinkInventoryModal from "../../../../Shared/RelinkInventoryModal";
import UnlinkInventoryModal from "../../../../Shared/UnlinkInventoryModal";
import useStyles from "./style";

interface Props {
  quote?: SalesQuote;
  errors: any;
  update: (attribute: string, value: any, noChanges?: boolean) => void;
  externalList?: ExternalList;
}

const QuoteMaintenanceDetailsForm = ({ quote, errors, update, externalList }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const classes = useStyles();

  const measurement = getMeasurement();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [jobType, setJobType] = useState<JobType[]>(externalList?.jobTypeList || []);
  const [leadSourceFromList, setLeadSourceFromList] = useState<LeadSource[]>(
    externalList?.leadSourceFromList || []
  );
  const [jobModeList, setJobModeList] = useState<JobMode[]>(externalList?.jobModeList || []);
  const [allUsers, setAllUsers] = useState<User[]>(externalList?.userList || []);
  const [statuses, setStatuses] = useState<Statuses[]>(externalList?.statusList || []);

  const [inventoryToUnlink, setInventoryToUnlink] = useState<InventoryLink | undefined>();
  const [inventoryToRelink, setInventoryToRelink] = useState<InventoryLink | undefined>();

  const [customVolumeQuotedText, setCustomVolumeQuotedText] = useState<string | undefined>();

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  useEffect(() => {
    if (customVolumeQuotedText === undefined)
      setCustomVolumeQuotedText(
        handleGetConvertValue(
          quote?.customVolumeQuoted || 0,
          measurement?.code,
          measurement?.decimalPlaces
        ).toString()
      );
  }, [quote?.customVolumeQuoted]);

  const quotedInventoryIsLocked: boolean = useMemo(() => {
    if (
      (quote?.customQuantityQuoted === null || quote?.customQuantityQuoted === undefined) &&
      (quote?.customVolumeQuoted === null || quote?.customVolumeQuoted === undefined)
    ) {
      return true;
    }

    return false;
  }, [quote?.customQuantityQuoted, quote?.customVolumeQuoted]);

  useQuery(
    ["branches.list"],
    () =>
      BranchService.getAll({
        id: JSON.stringify([
          ...currentUser?.branches?.map((branch) => branch.id)!,
          quote?.branchFromId,
          quote?.toBranchId,
        ]),
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "ASC"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: true,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) setBranches(response.data);
      },
    }
  );

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.jobTypeList,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobType(response.data);
      },
    }
  );

  useQuery(
    ["leadSourceFrom.list"],
    () =>
      LeadSearchFromService.getAll({
        status: Status.ACTIVE,
        sortBy: JSON.stringify(["name"]),
        companyId: currentUser?.companyId,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.leadSourceFromList,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0)
          setLeadSourceFromList(response.data);
      },
    }
  );

  useQuery(
    ["jobMode.list"],
    () =>
      ModeJobService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.jobModeList,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobModeList(response.data);
        }
      },
    }
  );

  useQuery(
    ["users.getAll.quote"],
    () =>
      UserService.getAll({
        include: JSON.stringify([
          { association: "role" },
          { association: "location" },
          {
            association: "usersCompanies",
            required: true,
            include: [{ association: "manager" }],
            where: {
              companyId: currentUser?.companyId,
              status: Status.ACTIVE,
              typeUser: [UserTypes.ADMIN, UserTypes.USER, UserTypes.SALES],
            },
          },
        ]),
        sortBy: JSON.stringify(["name"]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.userList,
      onSuccess: (response) => {
        if (response && response.data) setAllUsers(response.data);
      },
    }
  );
  useQuery(
    ["statuses.quote.list"],
    () =>
      StatusesService.getAll({
        usedBy: UsedByStatuses.QUOTES,
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !externalList?.statusList && !currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setStatuses(response.data);
        }
      },
    }
  );

  const totalQuotedItems = useMemo(() => {
    let total = 0;

    if (quote && quote.inventory && quote.inventory.length > 0) {
      quote.inventory.forEach((inv) => {
        if (inv.itemsJob && inv.itemsJob.length > 0) {
          inv.itemsJob.forEach((item) => {
            if (
              item?.itemJob?.destination?.name?.toLowerCase() !==
              DESTINATION.NOT_GOING?.toLowerCase()
            ) {
              total += Number(item.itemJob.quantity);
            }
          });
        }
        if (inv.materials && inv.materials.length > 0) {
          inv.materials.forEach((material) => {
            if (
              material?.serviceBooking?.isItem &&
              material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
            ) {
              total += Number(material.quantity);
            }
          });
        }
      });
    }

    return total;
  }, [quote?.inventory]);

  const totalQuotedVolume = useMemo(() => {
    let total = 0;

    if (quote && quote.inventory && quote.inventory.length > 0) {
      quote.inventory.forEach((inv) => {
        if (inv && inv.itemsJob && inv.itemsJob.length > 0) {
          inv.itemsJob.forEach((item) => {
            if (
              item?.itemJob?.destination?.name?.toLowerCase() !==
              DESTINATION.NOT_GOING?.toLowerCase()
            ) {
              total += Number(item.itemJob.volume) * Number(item.itemJob.quantity);
            }
          });
        }
        if (inv.materials && inv.materials.length > 0) {
          inv.materials.forEach((material) => {
            if (
              material?.serviceBooking?.isItem &&
              material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
            ) {
              total += Number(material.quantity) * Number(material.volume);
            }
          });
        }
      });
    }

    return total;
  }, [quote?.inventory]);

  const consultantName = useMemo(() => {
    if (quote?.user) {
      const name = quote?.user?.name;
      const lastName = quote?.user?.lastName;
      const email = quote?.user?.email;

      return `${name ?? ""} ${lastName ?? ""} - ${email ?? ""}`;
    }

    return "";
  }, [quote?.user]);

  const managerName = useMemo(() => {
    if (quote?.manager) {
      const name = quote?.manager?.name ?? "";
      const lastName = quote?.manager?.lastName ?? "";
      const email = quote?.manager?.email ?? "";

      const fullName = `${name} ${lastName}`;
      const hasNameAndEmail = fullName !== "" && email !== "";

      return `${fullName}${hasNameAndEmail ? " - " : ""}${email}`;
    }

    return "";
  }, [quote?.manager]);

  useEffect(() => {
    if (externalList?.userList?.length) setAllUsers(externalList?.userList);
  }, [externalList?.userList]);

  useEffect(() => {
    if (!quote?.branchFromId && currentUser && currentUser?.branches?.length === 1) {
      update("branchFromId", currentUser?.branches?.[0]?.id);
    }
  }, [quote, currentUser]);

  const isReferenceNotAvailableToEdit = () => {
    if (!quote?.jobSubStatus) return false;

    return ![JobSubStatus.INSPECT, JobSubStatus.DRAFT].includes(
      quote?.jobSubStatus as JobSubStatus
    );
  };

  return (
    <Grid container direction="row" alignItems="flex-start" spacing={2}>
      <Grid item xs={12} className={classes.title}>
        <Typography color="#1D2939" fontFamily="Manrope" fontWeight={600} fontSize="22px">
          {translate("details")}
        </Typography>
      </Grid>
      <Grid item container alignItems="flex-start" direction="row" spacing={2}>
        <Grid item xs={12} md={3}>
          <Input
            required
            name="reference"
            label={translate("reference")}
            data-cy="reference"
            value={quote?.reference}
            type="text"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              update("reference", event.target.value)
            }
            error={fieldHasError("reference")}
            errorMessage={getErrorByField("reference")}
            tabIndex={2}
            disabled={isReferenceNotAvailableToEdit() && currentUser?.typeUser !== UserTypes.ADMIN}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Input
            name="secondReference"
            label={translate("secondReference")}
            placeholder={translate("secondReference")}
            value={quote?.secondReference}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              update("secondReference", event.target.value)
            }
            error={fieldHasError("secondReferenceField")}
            errorMessage={getErrorByField("secondReferenceField")}
            disabled={isReferenceNotAvailableToEdit() && currentUser?.typeUser !== UserTypes.ADMIN}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            required
            name="jobType"
            label={translate("quoteType")}
            defaultValue=""
            data-cy="job-type"
            value={quote?.jobTypeId}
            onChange={(event: SelectChangeEvent<any>) => update("jobTypeId", event.target.value)}
            error={fieldHasError("type")}
            errorMessage={getErrorByField("type")}
            tabIndex={1}
            displayEmpty
            placeholder={translate("selectAnOption")}
          >
            {jobType.map((type) => (
              <MenuItem key={type.name} value={type.id} data-cy={`job-type-value-${type.name}`}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            name="status"
            label={translate("status")}
            disabled
            value={quote?.jobSubStatus}
            onChange={(event: SelectChangeEvent<any>) => update("jobSubStatus", event.target.value)}
            error={fieldHasError("status")}
            errorMessage={getErrorByField("status")}
            displayEmpty
            placeholder={translate("selectAnOption")}
          >
            {statuses?.map((status) => (
              <MenuItem key={status.id} value={status.name}>
                {status.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid item container alignItems="flex-start" direction="row" spacing={2}>
        <Grid item xs={12} sm={6}>
          <AutoSuggest
            label={translate("tag")}
            placeholder={translate("pressEnterToAdd")}
            data-cy="tags"
            value={quote && quote.tag && quote.tag.length > 0 ? quote?.tag?.split(",") : []}
            multiple
            freeSolo
            getOptionLabel={(option: string) => option}
            onChange={(tags: string[]) => {
              update(
                "tag",
                tags
                  .map((tag) => {
                    return tag.trim();
                  })
                  .join(",")
              );
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Select
            required
            name="mode"
            data-cy="mode"
            label={translate("mode")}
            value={quote?.mode ?? ""}
            onChange={(e: SelectChangeEvent<any>) => update("mode", e.target.value)}
            error={fieldHasError("mode")}
            errorMessage={getErrorByField("mode")}
            tabIndex={7}
            displayEmpty
            placeholder={translate("selectAnOption")}
          >
            {jobModeList.map((mode) => (
              <MenuItem key={mode.id} value={mode.value} data-cy={`mode-value-${mode.name}`}>
                {translate(mode.value.toLowerCase()) || mode.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.title}>
        <Typography color="#1D2939" fontFamily="Manrope" fontWeight={600} fontSize="22px">
          {translate("roles")}
        </Typography>
      </Grid>
      <Grid item container alignItems="flex-start" direction="row" spacing={2}>
        <Grid item xs={12} md={3}>
          <Select
            name="branchOriginId"
            data-cy="branchOriginId"
            label={translate("branchOrigin")}
            value={quote?.branchFromId || ""}
            onChange={(event: SelectChangeEvent<any>) => update("branchFromId", event.target.value)}
            tabIndex={3}
            displayEmpty
            placeholder={translate("selectAnOption")}
          >
            {branches &&
              branches.map(
                (branch) =>
                  branch.name &&
                  branch.name.trim() !== "" && (
                    <MenuItem
                      key={branch.id}
                      value={branch.id}
                      data-cy={`branch-origin-value-${branch.name}`}
                    >
                      {branch.name}
                    </MenuItem>
                  )
              )}
          </Select>
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            name="branchDestinationId"
            data-cy="branchDestinationId"
            label={translate("branchDestination")}
            value={quote?.toBranchId || ""}
            onChange={(e: SelectChangeEvent<any>) => update("toBranchId", e.target.value)}
            tabIndex={4}
            displayEmpty
            placeholder={translate("selectAnOption")}
          >
            {branches &&
              branches.map(
                (branch) =>
                  branch.name &&
                  branch.name.trim() !== "" && (
                    <MenuItem
                      key={branch.id}
                      value={branch.id}
                      data-cy={`branch-destination-value-${branch.name}`}
                    >
                      {branch.name}
                    </MenuItem>
                  )
              )}
          </Select>
        </Grid>
      </Grid>
      <Grid item container alignItems="flex-start" direction="row" spacing={2}>
        <Grid item xs={12} md={3}>
          <AutoSuggest
            required
            label={translate("consultant")}
            placeholder={translate("searchConsultant")}
            data-cy="consultant"
            inputValue={consultantName}
            getOptionLabel={(user?: User) => {
              const name = user?.name || "";
              const lastName = user?.lastName || "";
              const email = user?.email || "";

              return `${name} ${lastName} - ${email}`;
            }}
            getOptionSelected={(option?: User, value?: User) => option?.id === value?.id}
            options={allUsers}
            onChange={(user: User) => {
              if (user) {
                update("all", {
                  ...quote,
                  user: { ...user },
                  userId: user.id,
                  manager: user?.usersCompanies?.[0]?.manager
                    ? { ...user.usersCompanies[0]?.manager }
                    : null,
                  managerId: user?.usersCompanies?.[0]?.manager?.id,
                });
              } else {
                update("all", {
                  ...quote,
                  user: null,
                  userId: null,
                  manager: null,
                  managerId: null,
                });
              }
            }}
            error={fieldHasError("consultant")}
            errorMessage={getErrorByField("consultant")}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <AutoSuggest
            label={translate("manager")}
            placeholder="Search manager"
            data-cy="manager"
            inputValue={managerName}
            getOptionLabel={(manager?: User) => {
              const name = manager?.name || "";
              const lastName = manager?.lastName || "";
              const email = manager?.email || "";
              const fullName = `${name} ${lastName}`;
              const hasNameAndEmail = fullName !== "" && email !== "";

              return `${fullName}${hasNameAndEmail ? " - " : ""}${email}`;
            }}
            getOptionSelected={(option: User, value: User) => option.id === value.id}
            options={allUsers}
            onChange={(manager: User) => {
              if (manager) {
                update("all", {
                  ...quote,
                  manager: { ...manager },
                  managerId: manager.id,
                });
              } else {
                update("all", {
                  ...quote,
                  manager: null,
                  managerId: null,
                });
              }
            }}
            error={fieldHasError("manager")}
            errorMessage={getErrorByField("manager")}
          />
        </Grid>
      </Grid>
      <Grid item container alignItems="flex-start" direction="row" spacing={2}>
        <Grid item xs={12} md={3}>
          <Select
            name="leadSource"
            data-cy="leadSource"
            label={translate("leadSourceFrom")}
            value={quote?.leadSourceFrom ?? ""}
            onChange={(e: SelectChangeEvent<any>) => update("leadSourceFrom", e.target.value)}
            tabIndex={6}
            displayEmpty
            placeholder={translate("selectAnOption")}
          >
            {leadSourceFromList.map((item) => (
              <MenuItem key={item.id} value={item.value} data-cy={`lead-source-value-${item.name}`}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.title}>
        <Typography color="#1D2939" fontFamily="Manrope" fontWeight={600} fontSize="22px">
          {translate("weightAndMeasurements")}
        </Typography>
      </Grid>
      <Grid
        item
        container
        alignItems="flex-end"
        direction="row"
        spacing={2}
        justifyContent="flex-start"
      >
        <Grid item xs={12} md={3}>
          <Input
            name="allowance"
            label={translate("allowance")}
            placeholder={translate("allowance")}
            value={quote?.allowance}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              update("allowance", event.currentTarget.value);
            }}
          />
        </Grid>
      </Grid>
      <Grid container item direction="row" alignItems="center">
        <span className={classes.inventoryQuotedTitle}>{translate("inventoryQuoted")}</span>
        <Tooltip
          placement="right"
          title={
            quotedInventoryIsLocked ? translate("inventoryLinked") : translate("inventoryUnlinked")
          }
        >
          <IconButton
            onClick={() => {
              if (quotedInventoryIsLocked) {
                setInventoryToUnlink(InventoryLink.QUOTED);
              } else {
                setInventoryToRelink(InventoryLink.QUOTED);
              }
            }}
          >
            {quotedInventoryIsLocked ? (
              <HiOutlineLockClosed size={16} color="#475467" />
            ) : (
              <HiOutlineLockOpen size={16} color="#475467" />
            )}
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item container alignItems="flex-start" direction="row" spacing={2}>
        <Grid item xs={12} md={3}>
          <Input
            label={translate("items")}
            name="itemQuoted"
            type="number"
            placeholder={translate("noItems")}
            disabled={quotedInventoryIsLocked}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { isValid, value } = limitDecimalPlaces(event.target.value || "0", 0);

              if (isValid) update("customQuantityQuoted", value);
            }}
            value={
              quotedInventoryIsLocked
                ? totalQuotedItems.toString()
                : (quote?.customQuantityQuoted || 0)?.toString()
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Input
            label={translate("volume")}
            name="customVolumeQuoted"
            placeholder={Number(0)?.toFixed(measurement?.decimalPlaces)}
            disabled={quotedInventoryIsLocked}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const maskedValue = decimalMask(event.target.value, measurement?.decimalPlaces);

              setCustomVolumeQuotedText(maskedValue);

              const { isValid, value } = limitDecimalPlaces(
                maskedValue || "0",
                measurement?.decimalPlaces
              );

              const convertedValue = handleSaveConvertValue(value.toString(), measurement?.code);

              if (isValid) {
                update("customVolumeQuoted", convertedValue);
              }
            }}
            rightIcon={measurement?.code || ""}
            value={
              quotedInventoryIsLocked
                ? handleGetConvertValue(
                    Number(totalQuotedVolume),
                    measurement?.code || "m3",
                    measurement?.decimalPlaces
                  ).toString()
                : customVolumeQuotedText
            }
          />
        </Grid>
      </Grid>
      <UnlinkInventoryModal
        open={!!inventoryToUnlink}
        onClose={() => setInventoryToUnlink(undefined)}
        onSave={() => {
          update("all", {
            ...quote,
            customQuantityQuoted: 0,
            customVolumeQuoted: handleGetConvertValue(
              0.0,
              measurement?.code,
              measurement?.decimalPlaces
            ),
          });
          setCustomVolumeQuotedText(Number(0)?.toFixed(measurement?.decimalPlaces));
          setInventoryToUnlink(undefined);
        }}
      />
      <RelinkInventoryModal
        open={!!inventoryToRelink}
        onClose={() => setInventoryToRelink(undefined)}
        onSave={() => {
          update("all", {
            ...quote,
            customQuantityQuoted: null,
            customVolumeQuoted: null,
          });
          setInventoryToRelink(undefined);
        }}
      />
    </Grid>
  );
};

export default QuoteMaintenanceDetailsForm;
